import type { ReactNode } from 'react';
import { ListChecks, Signature as SignatureIcon } from 'lucide-react';
import { Box, Value } from '@/components/dashboard/DashboardParts';
import { DetailHeader } from '@/components/detail/DetailHeader';
import type { BackLinkProps } from '@/components/detail/BackLink';
import { Panel } from '@/components/Panel';
import { TeamTag } from '@/components/teams/TeamTag';
import { AbsenceBadge } from '@/components/timesheet/AbsenceBadge';
import { ABSENCE_SIGNATURE_PENDING, SignatureCell } from '@/components/timesheet/SignatureCell';
import { SignatureImage } from '@/components/timesheet/SignatureImage';
import { TimeSheetPdfButtons } from '@/components/timesheet/TimeSheetPdfButtons';
import { TimeEntryCard, TimeEntryCards, TimeEntryCells, TimeEntryHead } from '@/components/timesheet/TimeEntryTable';
import { TimeSheetStatusBadge } from '@/components/timesheet/TimeSheetStatusBadge';
import { TimeSheetStatusHistoryBox } from '@/components/timesheet/TimeSheetStatusHistoryBox';
import { useDataVersion } from '@/hooks/useDataVersion';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { formatMinutes } from '@/lib/duration';
import type { TimeSheetDetailData } from './timeSheetDetailData';

/**
 * Détail d'une feuille d'heures, pour l'employé (Mes demandes) comme pour le Dirigeant (Validation) : en-tête (statut,
 * total, lignes, équipe figée à la soumission, date de soumission ; l'employé et les actions selon l'écran), lignes en
 * lecture, signatures (Employé, Manager, Dirigeant) et historique du statut, avec le motif des refus. PDF téléchargeable
 * dès la soumission (§3.2), avant les actions de l'écran.
 */
export function TimeSheetDetailView({
  data,
  back,
  title,
  actions,
}: {
  data: TimeSheetDetailData;
  back: BackLinkProps;
  /** Titre de l'en-tête (le mois, ou l'employé et le mois) */
  title: ReactNode;
  actions?: ReactNode;
}) {
  const { sheet, entries, signatures } = data;
  // Historique rechargé avec la feuille (après une signature, un refus)
  const historyVersion = useDataVersion(data);
  const isMobile = useIsMobile();

  return (
    <div className="space-y-6">
      <DetailHeader
        back={back}
        label="Demande"
        title={title}
        subtitle={
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <TimeSheetStatusBadge reference={sheet.timeSheetStatusReference} label={sheet.timeSheetStatusLabel} />
            {sheet.isAbsence && <AbsenceBadge />}
          </div>
        }
        actions={
          <>
            {!sheet.isDraft && <TimeSheetPdfButtons sheetId={sheet.id} />}
            {actions}
          </>
        }
      >
        <Value label="Total heures">
          <span className="text-2xl font-bold">{formatMinutes(sheet.totalMinutes)}</span>
        </Value>
        <Value label="Lignes">
          <span className="text-2xl font-bold">{entries.length}</span>
        </Value>
        <Value label="Équipe">{sheet.teamName ? <TeamTag name={sheet.teamName} color={sheet.teamColor} /> : '–'}</Value>
        <Value label={sheet.isAbsence ? 'Déclarée le' : 'Soumise le'}>
          {/* Matérialisée sur la feuille (TimeSheet::submittedAt) : vidée au retour en brouillon après un refus */}
          {sheet.submittedAt
            ? sheet.submittedAt.format(DATE_TIME_FORMAT)
            : sheet.isAbsence
              ? 'Absence déclarée'
              : 'Pas encore soumise'}
        </Value>
      </DetailHeader>

      <Panel as="section" aria-label="Détail des heures déclarées" className="overflow-hidden">
        <h3 className="px-6 py-4 border-b border-slate-200 bg-slate-50/50 flex items-center gap-2 font-semibold text-slate-900">
          <ListChecks className="w-4 h-4 text-slate-400" />
          Détail des heures déclarées
        </h3>
        {entries.length && isMobile ? (
          <>
            <TimeEntryCards>
              {entries.map((entry) => (
                <TimeEntryCard key={entry.id} entry={entry} />
              ))}
            </TimeEntryCards>
            <p className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex justify-between text-sm font-semibold text-slate-900">
              Total
              <span data-testid="request-total">{formatMinutes(sheet.totalMinutes)}</span>
            </p>
          </>
        ) : entries.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[800px]">
              <TimeEntryHead />
              <tbody className="divide-y divide-slate-200">
                {entries.map((entry) => (
                  <tr key={entry.id} className="text-sm text-slate-700" data-testid="time-entry-row">
                    <TimeEntryCells entry={entry} />
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200 text-sm font-semibold text-slate-900">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-right">
                    Total
                  </td>
                  <td colSpan={4} className="px-4 py-3" data-testid="request-total">
                    {formatMinutes(sheet.totalMinutes)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="p-6 text-sm text-slate-500">
            {sheet.isDraft
              ? "Aucune ligne d'heure saisie pour l'instant."
              : sheet.isAbsence
                ? 'Absence : aucune heure à déclarer.'
                : 'Aucune heure supplémentaire déclarée.'}
          </p>
        )}
      </Panel>

      {/* Signatures et historique côte à côte sur grand écran, l'un sous l'autre sinon */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <Box title="Signatures" icon={SignatureIcon} empty={false}>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-3" data-testid="signatures">
            {(
              [
                ['Employé', signatures.employee, sheet.isAbsence ? ABSENCE_SIGNATURE_PENDING : undefined],
                ['Manager', signatures.manager, undefined],
                ['Dirigeant', signatures.director, undefined],
              ] as const
            ).map(([label, signature, pending]) => (
              <div key={label}>
                <dt className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</dt>
                <dd>
                  <SignatureCell signature={signature} pending={pending} preview={false} />
                  {signature && <SignatureImage signatureUrn={signature.urn} label={label} />}
                </dd>
              </div>
            ))}
          </dl>
        </Box>

        <TimeSheetStatusHistoryBox sheetId={sheet.id} version={historyVersion} />
      </div>
    </div>
  );
}
